import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from './api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'operator' | 'analyst';
}

interface AuthState {
  user: SessionUser | null;
  organization: { id: string; name: string } | null;
  loading: boolean;
  /** Why the last Microsoft sign-in round trip failed, for the login screen. */
  ssoError: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [organization, setOrganization] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const loadSession = useCallback(async () => {
    // Returning from Microsoft: the API redirects to /#sso=<one-time code>
    // (or /#sso_error=<message>). Swap the code for a session token, and
    // strip the fragment so a reload or a shared URL never replays it.
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const ssoCode = hash.get('sso');
    const ssoError = hash.get('sso_error');
    if (ssoCode || ssoError) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (ssoError) setSsoError(ssoError);
    if (ssoCode) {
      try {
        const result = await api.post<{ token: string }>('/api/auth/microsoft/exchange', { code: ssoCode });
        setToken(result.token);
      } catch (err) {
        setSsoError(err instanceof Error ? err.message : 'Microsoft sign-in failed');
      }
    }

    if (!getToken()) {
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<{ user: SessionUser | null; organization: { id: string; name: string } | null }>(
        '/api/auth/me'
      );
      setUser(me.user);
      setOrganization(me.organization);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSession();
    const onUnauthorized = () => setUser(null);
    window.addEventListener('emailblast:unauthorized', onUnauthorized);
    return () => window.removeEventListener('emailblast:unauthorized', onUnauthorized);
  }, [loadSession]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<{ token: string; user: SessionUser }>('/api/auth/login', {
        email,
        password,
      });
      setToken(result.token);
      setUser(result.user);
      await loadSession();
    },
    [loadSession]
  );

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, organization, loading, ssoError, login, logout }),
    [user, organization, loading, ssoError, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
